// Facebook OAuth Service for frontend-react
// Uses Conversations API for Facebook authentication
// MOCK MODE: Currently using mock implementation with localStorage persistence

import { CONVERSATIONS_API_URL, META_INTEGRATION_ENABLED, buildMetaGraphUrl, getConversationsAuthToken } from '@/config/env'

export interface FacebookUserInfo {
  id: string
  name: string
  email?: string
  picture?: {
    data: {
      url: string
    }
  }
}

export interface FacebookConnectionStatus {
  connected: boolean
  needs_reconnect?: boolean
  code?: string
  message?: string
  user?: FacebookUserInfo
  access_token?: string
  selected_page?: {
    id: string
    name: string
    access_token: string
  }
}

export interface FacebookPage {
  id: string
  name: string
  access_token: string
  category?: string
  tasks?: string[]
}

export interface FacebookBusiness {
  id: string
  name: string
}

export interface WhatsAppBusinessAccount {
  id: string
  name: string
  currency: string
  timezone_id: string
  message_template_namespace: string
}

export interface WhatsAppPhoneNumber {
  id: string
  verified_name: string
  display_phone_number: string
  quality_rating: string
  code_verification_status: string
}

export interface FacebookAdAccount {
  id: string
  account_id: string
  name: string
  currency: string
  account_status: number
}

export interface FacebookCampaign {
  id: string
  name: string
  status: string
  objective: string
}

export interface FacebookLead {
  id: string
  created_time: string
  field_data?: Array<{
    name: string
    values: string[]
  }>
}

export interface WhatsAppMessageTemplate {
  id?: string
  name: string
  status: string
  language: string
  category: string
  components?: unknown[]
}

export class FacebookServiceError extends Error {
  public status?: number
  
  constructor(message: string, status?: number) {
    super(message)
    this.status = status
    this.name = 'FacebookServiceError'
  }
}

// Mock data for development
const MOCK_USER_DATA: FacebookUserInfo = {
  id: "mock_facebook_user_123",
  name: "John Doe",
  email: "john.doe@example.com",
  picture: {
    data: {
      url: "https://api.dicebear.com/7.x/avataaars/svg?seed=JohnDoe"
    }
  }
}

const STORAGE_KEY = 'facebook_oauth_mock_state'

class FacebookServiceClient {
  private baseUrl: string
  private mockMode: boolean

  constructor() {
    this.baseUrl = CONVERSATIONS_API_URL || ''
    // Disable mock mode when API URL is configured (even in development)
    // Mock mode only enabled if API URL is completely missing
    // this.mockMode = !this.baseUrl
    this.mockMode = false // Force disable mock mode to test real integration
  }

  private getAuthToken(): string {
    return getConversationsAuthToken()
  }

  // Mock storage helpers
  private getMockState(): FacebookConnectionStatus | null {
    if (typeof window === 'undefined') return null
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        return JSON.parse(stored)
      } catch {
        return null
      }
    }
    return null
  }

  private setMockState(state: FacebookConnectionStatus | null): void {
    if (typeof window === 'undefined') return
    if (state) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }

  // Simulate API delay
  private async delay(ms: number = 1000): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    if (!META_INTEGRATION_ENABLED) {
      throw new FacebookServiceError('Meta integration is disabled in this environment', 503)
    }

    const url = `${this.baseUrl}${endpoint}`
    
    const config: RequestInit = {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options
    }

    // Add authentication token
    const token = this.getAuthToken()
    if (token) {
      config.headers = {
        ...config.headers,
        'Authorization': `Bearer ${token}`,
      }
    }

    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        const errorText = await response.text()
        throw new FacebookServiceError(
          `HTTP ${response.status}: ${errorText || response.statusText}`,
          response.status
        )
      }

      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json()
        return data
      } else {
        const text = await response.text()
        return text as T
      }
    } catch (error) {
      if (error instanceof FacebookServiceError) {
        throw error
      }
      throw new FacebookServiceError(`Network error: ${error}`, 0)
    }
  }

  /**
   * Connect Facebook account
   * Sends access token to backend for storage
   * MOCK: Simulates connection with mock data
   */
  async connectFacebook(accessToken: string): Promise<FacebookConnectionStatus> {
    if (this.mockMode) {
      // Simulate API delay
      await this.delay(1500)
      
      // Generate mock access token if not provided
      const mockToken = accessToken || `mock_token_${Date.now()}`
      
      const mockStatus: FacebookConnectionStatus = {
        connected: true,
        user: MOCK_USER_DATA,
        access_token: mockToken
      }
      
      // Store in localStorage
      this.setMockState(mockStatus)
      
      return mockStatus
    }

    return this.request<FacebookConnectionStatus>('/api/v1/auth/facebook/connect/', {
      method: 'POST',
      body: JSON.stringify({ access_token: accessToken })
    })
  }

  /**
   * Disconnect Facebook account
   * Revokes access and removes stored tokens
   * MOCK: Clears localStorage state
   */
  async disconnectFacebook(): Promise<void> {
    if (this.mockMode) {
      // Simulate API delay
      await this.delay(800)
      
      // Clear localStorage
      this.setMockState(null)
      
      return
    }

    return this.request<void>('/api/v1/auth/facebook/disconnect/', {
      method: 'POST'
    })
  }

  /**
   * Get Facebook connection status
   * Returns whether user is connected and user info if available
   * MOCK: Returns state from localStorage
   */
  async getFacebookStatus(): Promise<FacebookConnectionStatus> {
    if (this.mockMode) {
      // Simulate API delay
      await this.delay(500)
      
      const stored = this.getMockState()
      
      if (stored) {
        return stored
      }
      
      return {
        connected: false
      }
    }

    return this.request<FacebookConnectionStatus>('/api/v1/auth/facebook/status/')
  }

  /**
   * Get Facebook user information
   * Fetches current user profile data
   * MOCK: Returns mock user data if connected
   */
  async getFacebookUserInfo(): Promise<FacebookUserInfo> {
    if (this.mockMode) {
      // Simulate API delay
      await this.delay(500)
      
      const stored = this.getMockState()
      
      if (stored && stored.user) {
        return stored.user
      }
      
      throw new FacebookServiceError('User not connected', 404)
    }

    return this.request<FacebookUserInfo>('/api/v1/auth/facebook/user/')
  }

  /**
   * Get Facebook pages
   * Fetches list of pages associated with the user's access token
   */
  async getFacebookPages(accessToken?: string): Promise<FacebookPage[]> {
    if (this.mockMode) {
      // Simulate API delay
      await this.delay(800)
      
      // Return mock pages
      return [
        {
          id: 'mock_page_123',
          name: 'Mock Business Page',
          access_token: 'mock_page_token',
          category: 'Business',
          tasks: ['MANAGE', 'MESSAGING'],
        },
      ]
    }

    const body = accessToken ? { access_token: accessToken } : {}
    const response = await this.request<{ data: FacebookPage[] }>('/api/v1/auth/facebook/pages/', {
      method: 'POST',
      body: JSON.stringify(body),
    })
    return response.data || []
  }

  /**
   * Connect Facebook account with page selection
   * Sends access token and selected page info to backend
   */
  async connectFacebookWithPage(
    accessToken: string,
    selectedPageId?: string,
    pageAccessToken?: string,
    pageName?: string
  ): Promise<FacebookConnectionStatus> {
    const body: {
      access_token: string
      selected_page_id?: string
      page_access_token?: string
      page_name?: string
    } = {
      access_token: accessToken,
    }

    if (selectedPageId && pageAccessToken) {
      body.selected_page_id = selectedPageId
      body.page_access_token = pageAccessToken
      if (pageName) {
        body.page_name = pageName
      }
    }

    if (this.mockMode) {
      await this.delay(1500)
      
      const mockStatus: FacebookConnectionStatus = {
        connected: true,
        user: MOCK_USER_DATA,
        access_token: accessToken,
      }

      if (selectedPageId && pageAccessToken) {
        mockStatus.selected_page = {
          id: selectedPageId,
          name: pageName || 'Mock Page',
          access_token: pageAccessToken,
        }
      }

      this.setMockState(mockStatus)
      return mockStatus
    }

    return this.request<FacebookConnectionStatus>('/api/v1/auth/facebook/connect/', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  // --- Direct Graph API Helpers for WhatsApp Discovery ---

  /**
   * Get Business ID associated with a Page
   * Uses direct Graph API call
   */
  async getBusinessFromPage(pageId: string, userAccessToken: string): Promise<FacebookBusiness | null> {
    if (this.mockMode) return { id: 'mock_biz_123', name: 'Mock Business' }

    try {
      const response = await fetch(buildMetaGraphUrl(`${pageId}?fields=business&access_token=${userAccessToken}`))
      if (!response.ok) throw new Error(`Graph API Error: ${response.statusText}`)
      const data = await response.json()
      return data.business || null
    } catch (error) {
      console.error('Error fetching business from page:', error)
      return null
    }
  }

  /**
   * Get Client Business ID from User (Fallback)
   * Uses direct Graph API call
   */
  async getClientBusinessId(userAccessToken: string): Promise<string | null> {
    if (this.mockMode) return 'mock_biz_123'
    try {
      // Try to get client_business_id from /me endpoint
      // Note: This often requires a system user token or specific setup, but sometimes works for admin users
      const response = await fetch(buildMetaGraphUrl(`me?fields=client_business_id,id&access_token=${userAccessToken}`))
      if (!response.ok) return null 
      const data = await response.json()
      return data.client_business_id || null
    } catch (error) {
      console.error('Error fetching client business id:', error)
      return null
    }
  }

  /**
   * Get Owned WhatsApp Business Accounts for a Business
   * Uses direct Graph API call
   */
  async getOwnedWhatsAppBusinessAccounts(businessId: string, userAccessToken: string): Promise<WhatsAppBusinessAccount[]> {
    if (this.mockMode) return [{ id: 'mock_waba_123', name: 'Mock WABA', currency: 'USD', timezone_id: '1', message_template_namespace: 'ns' }]

    try {
      const response = await fetch(buildMetaGraphUrl(`${businessId}/owned_whatsapp_business_accounts?access_token=${userAccessToken}`))
      if (!response.ok) throw new Error(`Graph API Error: ${response.statusText}`)
      const data = await response.json()
      return data.data || []
    } catch (error) {
      console.error('Error fetching WABAs:', error)
      return []
    }
  }

  /**
   * Get Phone Numbers for a WABA
   * Uses direct Graph API call
   */
  async getWhatsAppPhoneNumbers(wabaId: string, userAccessToken: string): Promise<WhatsAppPhoneNumber[]> {
    if (this.mockMode) return [{ id: 'mock_phone_123', verified_name: 'Mock Phone', display_phone_number: '+1555000000', quality_rating: 'GREEN', code_verification_status: 'VERIFIED' }]

    try {
      const response = await fetch(buildMetaGraphUrl(`${wabaId}/phone_numbers?access_token=${userAccessToken}`))
      if (!response.ok) throw new Error(`Graph API Error: ${response.statusText}`)
      const data = await response.json()
      return data.data || []
    } catch (error) {
      console.error('Error fetching phone numbers:', error)
      return []
    }
  }

  // --- Facebook Ads and Leads API Methods ---

  /**
   * Get Facebook Ad Accounts
   * Fetches ad accounts using the credentials resolved server-side.
   */
  async getAdAccounts(): Promise<FacebookAdAccount[]> {
    if (this.mockMode) {
      await this.delay(800)
      return [
        {
          id: 'act_mock_123',
          account_id: '123456789',
          name: 'Mock Ad Account',
          currency: 'USD',
          account_status: 1,
        },
      ]
    }

    const response = await this.request<{ data: FacebookAdAccount[] }>('/api/v1/leads/meta/ad-accounts/', {
      method: 'GET',
    })
    return response.data || []
  }

  /**
   * Get Facebook Campaigns for an Ad Account
   * Fetches campaigns for a specific ad account using server-side credentials.
   */
  async getCampaigns(adAccountId: string): Promise<FacebookCampaign[]> {
    if (this.mockMode) {
      await this.delay(800)
      return [
        {
          id: 'campaign_mock_123',
          name: 'Mock Campaign',
          status: 'ACTIVE',
          objective: 'LEAD_GENERATION',
        },
      ]
    }

    const response = await this.request<{ data: FacebookCampaign[] }>(
      `/api/v1/leads/meta/campaigns/?ad_account_id=${encodeURIComponent(adAccountId)}`,
      {
        method: 'GET',
      }
    )
    return response.data || []
  }

  /**
   * Get Facebook Leads for a Lead Form
   * Fetches leads from a specific lead form
   * Note: Requires leads_retrieval permission
   */
  async getLeads(leadFormId: string, accessToken?: string): Promise<FacebookLead[]> {
    if (this.mockMode) {
      await this.delay(800)
      return [
        {
          id: 'lead_mock_123',
          created_time: new Date().toISOString(),
          field_data: [
            { name: 'full_name', values: ['John Doe'] },
            { name: 'email', values: ['john@example.com'] },
            { name: 'phone_number', values: ['+1234567890'] },
          ],
        },
      ]
    }

    // If no token provided, get it from status
    let token = accessToken
    if (!token) {
      const status = await this.getFacebookStatus()
      if (!status.access_token) {
        throw new FacebookServiceError('Access token not available', 401)
      }
      token = status.access_token
    }

    // Direct Graph API call for leads
    try {
      const response = await fetch(
        buildMetaGraphUrl(`${leadFormId}/leads?access_token=${token}&limit=100`)
      )
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new FacebookServiceError(
          errorData.error?.message || `Failed to fetch leads: ${response.statusText}`,
          response.status
        )
      }
      const data = await response.json()
      return data.data || []
    } catch (error) {
      if (error instanceof FacebookServiceError) {
        throw error
      }
      throw new FacebookServiceError(`Error fetching leads: ${error}`, 0)
    }
  }

  // --- WhatsApp Business Accounts and Templates ---

  /**
   * Get WhatsApp Business Accounts (WABAs)
   * Fetches WABAs using the credentials resolved server-side.
   */
  async getWhatsAppBusinessAccounts(): Promise<WhatsAppBusinessAccount[]> {
    if (this.mockMode) {
      await this.delay(800)
      return [
        {
          id: 'mock_waba_123',
          name: 'Mock WABA',
          currency: 'USD',
          timezone_id: '1',
          message_template_namespace: 'mock_namespace',
        },
      ]
    }

    const response = await this.request<{ data: Array<{ owned_whatsapp_business_accounts?: { data: WhatsAppBusinessAccount[] } }> }>(
      '/api/v1/leads/meta/waba/',
      {
        method: 'GET',
      }
    )

    // Parse the nested structure: data.data[].owned_whatsapp_business_accounts.data[]
    const businesses = response.data || []
    const allWabas: WhatsAppBusinessAccount[] = []

    businesses.forEach((biz) => {
      if (biz.owned_whatsapp_business_accounts?.data) {
        allWabas.push(...biz.owned_whatsapp_business_accounts.data)
      }
    })

    return allWabas
  }

  /**
   * Get WhatsApp Message Templates for a WABA
   * Fetches templates for a specific WhatsApp Business Account using server-side credentials.
   */
  async getWhatsAppTemplates(wabaId: string): Promise<WhatsAppMessageTemplate[]> {
    if (this.mockMode) {
      await this.delay(800)
      return [
        {
          name: 'mock_template',
          status: 'APPROVED',
          language: 'en',
          category: 'MARKETING',
          components: [],
        },
      ]
    }

    const response = await this.request<{ data: WhatsAppMessageTemplate[] }>(
      `/api/v1/leads/meta/templates/?waba_id=${encodeURIComponent(wabaId)}`,
      {
        method: 'GET',
      }
    )
    return response.data || []
  }
}

// Export singleton instance
export const facebookService = new FacebookServiceClient()
